import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangedChip,
  CurrentValuesBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  SectionLabel,
  ViewHeader,
} from "./EditDialogShared";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { useBatteryInfo, useVehicleInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { updateDigitalTwinDetails } from "../../Api/DigitalTwin";
import { getOrgLocationLevels, getOrgLocationMaster } from "../../Api/RbacAdminApi";
import { translateErrorMessage } from "../../Helper/Common/FieldTranslator";
import CODE from "../../Static/Constants/StatusCodes";

// Only these level types are supported for origin location editing
const SUPPORTED_LEVEL_TYPES = new Set(["country", "state", "city", "depot"]);

// Maps org location level type to the vehicleInfo / payload field name
function levelTypeToField(type) {
  if (!type) return null;
  const t = type.toLowerCase();
  if (t === "depot") return "originDepo";
  return `origin${t.charAt(0).toUpperCase()}${t.slice(1)}`;
}

// "depot" -> "Depot". Used for both the current-value rows and the input labels
// so the two halves of the view name each level identically.
function levelLabel(type) {
  if (!type) return "";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
}

// Exported for unit testing: the current-value rows shown above the selectors,
// one per configured level, in the org's own level order.
export function buildCurrentLocationRows(levels, vehicleInfo) {
  return (levels ?? []).map((level) => {
    const field = levelTypeToField(level.type);
    return {
      label: levelLabel(level.type),
      value: field ? (vehicleInfo?.[field] ?? null) : null,
    };
  });
}

// Exported for unit testing: whether a level's new selection differs from the
// value the vehicle currently carries. An empty selection is not a change -
// clearing a dropdown leaves the form incomplete rather than editing anything.
export function isLevelChanged(newValue, currentValue) {
  return !!newValue && newValue !== (currentValue ?? null);
}

const AUTOCOMPLETE_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    padding: "4px 8px !important",
    "& fieldset": { borderColor: "#E6E6E6", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "&.Mui-focused fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "& input": { fontSize: "14px", padding: "6px 4px !important" },
    "& input::placeholder": { color: "#A4B2C1", opacity: 1, fontSize: "14px" },
  },
};

// Applied to Autocomplete sx (not TextField) to ensure specificity over MUI's
// absolute-position default for endAdornment
const AUTOCOMPLETE_SX = {
  "& .MuiAutocomplete-endAdornment": {
    top: "50% !important",
    transform: "translateY(-50%) !important",
  },
};

export default function ChangeOriginLocationView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { batteryID } = useBatteryInfo();
  const vehicleInfo = useVehicleInfo();

  // Snapshotted on mount: onSuccess refreshes the vehicle context before the
  // dialog closes, and the current column must keep reading "current" while the
  // user edits a dropdown rather than following the selection.
  const currentVehicleInfoRef = useRef(vehicleInfo);
  const currentVehicleInfo = currentVehicleInfoRef.current;

  const [levels, setLevels] = useState([]);       // sorted level definitions
  const [masterRows, setMasterRows] = useState([]); // flat location master records
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(false);

  // selection keyed by level key: { level1: "India", level2: "Maharashtra", ... }
  const [selection, setSelection] = useState({});

  const [remark, setRemark] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setDataLoading(true);
      setDataError(false);
      try {
        const [levelsRes, masterRes] = await Promise.all([
          getOrgLocationLevels(),
          getOrgLocationMaster(),
        ]);

        if (cancelled) return;

        if (
          levelsRes?.responseStatus?.code !== CODE.SUCCESS ||
          masterRes?.responseStatus?.code !== CODE.SUCCESS
        ) {
          setDataError(true);
          return;
        }

        const rawLevels = levelsRes?.response?.data ?? [];
        const sorted = [...rawLevels]
          .filter((l) => SUPPORTED_LEVEL_TYPES.has(l.type?.toLowerCase()))
          .sort((a, b) => {
            const ai = parseInt(a.level.replace("level", ""), 10);
            const bi = parseInt(b.level.replace("level", ""), 10);
            return ai - bi;
          });

        const rows = masterRes?.response?.data ?? [];

        // Pre-populate selection from current vehicleInfo values
        const initial = {};
        sorted.forEach((l) => {
          const field = levelTypeToField(l.type);
          initial[l.level] = field ? (currentVehicleInfo[field] ?? null) : null;
        });

        setLevels(sorted);
        setMasterRows(rows);
        setSelection(initial);
      } catch {
        if (!cancelled) setDataError(true);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One row per configured level, so a three-level org shows three rows.
  const currentRows = useMemo(
    () => buildCurrentLocationRows(levels, currentVehicleInfo),
    [levels, currentVehicleInfo]
  );

  // Derive dropdown options for each level based on current selection
  const optionsPerLevel = useMemo(() => {
    return levels.map((level, idx) => {
      const filtered = masterRows.filter((row) => {
        for (let i = 0; i < idx; i++) {
          if (row[levels[i].level] !== selection[levels[i].level]) return false;
        }
        return true;
      });
      const unique = [...new Set(filtered.map((r) => r[level.level]).filter(Boolean))];
      return unique.sort();
    });
  }, [levels, masterRows, selection]);

  const handleSelect = (levelIdx, value) => {
    setSelection((prev) => {
      const next = { ...prev, [levels[levelIdx].level]: value };
      // Clear all downstream selections
      for (let i = levelIdx + 1; i < levels.length; i++) {
        next[levels[i].level] = null;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, location: undefined }));
  };

  const allLevelsSelected = levels.length > 0 && levels.every((l) => !!selection[l.level]);

  const handleSubmit = async () => {
    const errs = {};
    if (!allLevelsSelected) errs.location = "Please select all location fields";
    if (!remark.trim()) errs.remark = true;
    if (remark.length > MAX_REMARK_LENGTH) errs.remarkCount = true;

    setErrors(errs);
    if (Object.keys(errs).length) return;

    // Build payload: { originCountry: "...", originState: "...", ... }
    const locationPayload = {};
    levels.forEach((l) => {
      const field = levelTypeToField(l.type);
      if (field && selection[l.level]) {
        locationPayload[field] = selection[l.level];
      }
    });

    setIsLoading(true);
    try {
      const response = await updateDigitalTwinDetails(batteryID, {
        ...locationPayload,
        remarks: remark,
      });

      if (response?.responseStatus?.code !== 200) {
        throw new Error(response?.responseStatus?.msg);
      }

      await onSuccess("Origin Location Updated!");
    } catch (error) {
      const msg = translateErrorMessage(error?.message);
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <ViewHeader title="Change Origin Location" onBack={onBack} />

      {dataLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} sx={{ color: "#6D72F6" }} />
        </Box>
      )}

      {dataError && !dataLoading && (
        <Typography sx={{ fontSize: "14px", color: "#930500", mb: 2 }}>
          Failed to load location data. Please close and try again.
        </Typography>
      )}

      {!dataLoading && !dataError && levels.length === 0 && (
        <Typography sx={{ fontSize: "14px", color: "#6B7280", mb: 2 }}>
          Origin location editing is not configured for this organisation.
        </Typography>
      )}

      {!dataLoading && !dataError && (
        <>
          {/* An org with no supported levels has nothing current to show and no
              inputs to label, so neither header is rendered. */}
          {levels.length > 0 && (
            <>
              <CurrentValuesBox
                label="Current Origin Location"
                rows={currentRows}
                columns={2}
              />

              <SectionLabel>New Origin Location</SectionLabel>
            </>
          )}

          {/* Two per row: four full-width selectors pushed the remark field and
              Confirm Change below the fold on a short screen. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 2,
              rowGap: 2,
              mb: 2,
            }}
          >
            {levels.map((level, idx) => {
              const label = levelLabel(level.type);
              const options = optionsPerLevel[idx] ?? [];
              const value = selection[level.level] ?? "";
              const isDisabled = idx > 0 && !selection[levels[idx - 1].level];
              const isChanged = isLevelChanged(value, currentRows[idx]?.value);

              return (
                <Box key={level.level} sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Typography
                      variant="fw400s14c000000"
                      sx={{ lineHeight: "24px", color: "#494949" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="fw400s14c000000"
                      sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
                    >
                      &nbsp;*
                    </Typography>
                    {isChanged && <ChangedChip />}
                  </Box>
                  <Autocomplete
                    fullWidth
                    disabled={isDisabled}
                    options={options}
                    value={value || null}
                    onChange={(_, newValue) => handleSelect(idx, newValue)}
                    disableClearable={false}
                    sx={AUTOCOMPLETE_SX}
                    PaperComponent={(props) => (
                      <Paper
                        {...props}
                        sx={{
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          "& .MuiAutocomplete-option": { fontSize: "14px" },
                        }}
                      />
                    )}
                    slotProps={{
                      listbox: { sx: { fontSize: "14px" } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`Search or select ${label}`}
                        sx={AUTOCOMPLETE_INPUT_SX}
                      />
                    )}
                  />
                </Box>
              );
            })}
          </Box>

          {errors.location && (
            <Typography sx={{ fontSize: "10px", color: "#930500", mb: 1.5 }}>
              {errors.location}
            </Typography>
          )}

          <RemarkField
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            error={errors.remark}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryFormLoadingActionButton
              disabled={isLoading || !allLevelsSelected}
              onClick={handleSubmit}
              loading={isLoading}
              sx={{ borderRadius: "4px", px: 3 }}
            >
              Confirm Change
            </PrimaryFormLoadingActionButton>
          </Box>
        </>
      )}
    </Box>
  );
}
